import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Users, Building2, Plus, Trash2, Edit, Bell
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { useAuth } from "../App";

const SettingsPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    email_task_reminders: true,
    email_overdue_alerts: true,
    email_site_visit_reminders: true,
    email_lead_assignments: true,
    reminder_hours_before: 24
  });
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [newProject, setNewProject] = useState({
    name: "",
    location: "",
    price_min: "",
    price_max: "",
    configurations: [],
    description: "",
    amenities: []
  });

  useEffect(() => {
    fetchData();
    fetchNotificationSettings();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, projectsRes] = await Promise.all([
        axios.get(`${API}/auth/users`),
        axios.get(`${API}/projects`)
      ]);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error("Fetch data error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API}/notifications/settings`);
      setNotificationSettings(response.data);
    } catch (error) {
      console.error("Fetch notification settings error:", error);
    }
  };

  const handleSaveNotificationSettings = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(`${API}/notifications/settings`, notificationSettings);
      toast.success("Notification settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/auth/users/${userId}/role?role=${newRole}`);
      toast.success("Role updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name || !newProject.location) {
      toast.error("Name and location are required");
      return;
    }

    try {
      await axios.post(`${API}/projects`, {
        ...newProject,
        price_min: parseFloat(newProject.price_min) || 0,
        price_max: parseFloat(newProject.price_max) || 0,
        configurations: newProject.configurations.filter(c => c),
        amenities: newProject.amenities.filter(a => a)
      });
      toast.success("Project created");
      setShowProjectDialog(false);
      setNewProject({
        name: "", location: "", price_min: "", price_max: "",
        configurations: [], description: "", amenities: []
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create project");
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-purple-100 text-purple-700",
      sales_head: "bg-blue-100 text-blue-700",
      sales_rep: "bg-slate-100 text-slate-700"
    };
    return styles[role] || styles.sales_rep;
  };

  const formatINR = (amount) => {
    if (!amount) return "N/A";
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Manrope']">Settings</h1>
          <p className="text-slate-500 mt-1">Manage team and projects</p>
        </div>

        <Tabs defaultValue="team" className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="team" data-testid="team-tab">
              <Users className="w-4 h-4 mr-2" />
              Team
            </TabsTrigger>
            <TabsTrigger value="projects" data-testid="projects-tab">
              <Building2 className="w-4 h-4 mr-2" />
              Projects
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="notifications-tab">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
          </TabsList>

          <TabsContent value="team" className="mt-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">Team Members</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-16 bg-slate-100 rounded animate-pulse"></div>
                    ))}
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {users.map((u) => (
                      <div key={u.user_id} className="py-4 flex items-center justify-between" data-testid={`user-${u.user_id}`}>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                            {u.picture ? (
                              <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-slate-600 font-medium">{u.name?.charAt(0)}</span>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{u.name}</p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {user?.role === 'admin' && u.user_id !== user.user_id ? (
                            <Select value={u.role} onValueChange={(v) => handleRoleChange(u.user_id, v)}>
                              <SelectTrigger className="w-36">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="sales_head">Sales Head</SelectItem>
                                <SelectItem value="sales_rep">Sales Rep</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getRoleBadge(u.role)}`}>
                              {u.role?.replace(/_/g, ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {users.length === 0 && (
                      <p className="py-8 text-center text-slate-500">No team members yet</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="projects" className="mt-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold font-['Manrope']">Projects</CardTitle>
                {user?.role === 'admin' && (
                  <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="add-project-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Project
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="font-['Manrope']">Add New Project</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Project Name *</Label>
                          <Input
                            placeholder="e.g., Skyline Towers"
                            value={newProject.name}
                            onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Location *</Label>
                          <Input
                            placeholder="e.g., Andheri West, Mumbai"
                            value={newProject.location}
                            onChange={(e) => setNewProject({...newProject, location: e.target.value})}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Price Min (₹)</Label>
                            <Input
                              type="number"
                              placeholder="e.g., 5000000"
                              value={newProject.price_min}
                              onChange={(e) => setNewProject({...newProject, price_min: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Price Max (₹)</Label>
                            <Input
                              type="number"
                              placeholder="e.g., 15000000"
                              value={newProject.price_max}
                              onChange={(e) => setNewProject({...newProject, price_max: e.target.value})}
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Configurations (comma separated)</Label>
                          <Input
                            placeholder="1BHK, 2BHK, 3BHK"
                            value={newProject.configurations.join(', ')}
                            onChange={(e) => setNewProject({...newProject, configurations: e.target.value.split(',').map(c => c.trim())})}
                          />
                        </div>
                        <div>
                          <Label>Amenities (comma separated)</Label>
                          <Input
                            placeholder="Pool, Gym, Clubhouse"
                            value={newProject.amenities.join(', ')}
                            onChange={(e) => setNewProject({...newProject, amenities: e.target.value.split(',').map(a => a.trim())})}
                          />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Textarea
                            placeholder="Project description..."
                            value={newProject.description}
                            onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                            rows={3}
                          />
                        </div>
                        <Button onClick={handleCreateProject} className="w-full">Create Project</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-24 bg-slate-100 rounded animate-pulse"></div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {projects.map((project) => (
                      <Card key={project.project_id} className="border-slate-200" data-testid={`project-${project.project_id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-semibold text-slate-900">{project.name}</h3>
                              <p className="text-sm text-slate-500">{project.location}</p>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              project.status === 'active' ? 'bg-green-100 text-green-700' :
                              project.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {project.status}
                            </span>
                          </div>
                          <div className="space-y-1 text-sm">
                            <p className="text-slate-600">
                              <span className="text-slate-500">Price:</span> {formatINR(project.price_min)} - {formatINR(project.price_max)}
                            </p>
                            {project.configurations?.length > 0 && (
                              <p className="text-slate-600">
                                <span className="text-slate-500">Config:</span> {project.configurations.join(', ')}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {projects.length === 0 && (
                      <p className="col-span-full py-8 text-center text-slate-500">
                        No projects added yet
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="mt-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">Email Notifications</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">Task Reminders</p>
                      <p className="text-sm text-slate-500">Get notified about upcoming tasks</p>
                    </div>
                    <Switch
                      checked={notificationSettings.email_task_reminders}
                      onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, email_task_reminders: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">Overdue Alerts</p>
                      <p className="text-sm text-slate-500">Get notified when tasks are overdue</p>
                    </div>
                    <Switch
                      checked={notificationSettings.email_overdue_alerts}
                      onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, email_overdue_alerts: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">Site Visit Reminders</p>
                      <p className="text-sm text-slate-500">Get reminded about scheduled site visits</p>
                    </div>
                    <Switch
                      checked={notificationSettings.email_site_visit_reminders}
                      onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, email_site_visit_reminders: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">Lead Assignment</p>
                      <p className="text-sm text-slate-500">Get notified when leads are assigned to you</p>
                    </div>
                    <Switch
                      checked={notificationSettings.email_lead_assignments}
                      onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, email_lead_assignments: checked})}
                    />
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <Label>Reminder Time (hours before)</Label>
                  <Select 
                    value={String(notificationSettings.reminder_hours_before)} 
                    onValueChange={(v) => setNotificationSettings({...notificationSettings, reminder_hours_before: parseInt(v)})}
                  >
                    <SelectTrigger className="w-40 mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 hour</SelectItem>
                      <SelectItem value="2">2 hours</SelectItem>
                      <SelectItem value="6">6 hours</SelectItem>
                      <SelectItem value="12">12 hours</SelectItem>
                      <SelectItem value="24">24 hours</SelectItem>
                      <SelectItem value="48">48 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  onClick={handleSaveNotificationSettings}
                  disabled={savingNotifications}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  {savingNotifications ? "Saving..." : "Save Settings"}
                </Button>
                
                <p className="text-xs text-slate-500">
                  Note: Email notifications require Resend API key to be configured. Contact admin if emails are not being delivered.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default SettingsPage;
